// apps/frontend/src/lib/validation-utils.ts
import type { PipelineNode, PipelineEdge } from '@/pages/pipelines/types/pipeline';

// Health score constants
const ERROR_PENALTY = 20;
const ERROR_CAP = 80;
const WARNING_PENALTY = 5;
const WARNING_CAP = 30;
const COMPLEXITY_THRESHOLD = 20;
const COMPLEXITY_PENALTY = 1;
const COMPLEXITY_CAP = 10;

/**
 * Calculate pipeline health score (0-100)
 *
 * Scoring rules:
 * - Base score: 100
 * - Errors: -ERROR_PENALTY points each (capped at -ERROR_CAP)
 * - Warnings: -WARNING_PENALTY points each (capped at -WARNING_CAP)
 * - Complexity penalty: -COMPLEXITY_PENALTY point per node over COMPLEXITY_THRESHOLD (capped at -COMPLEXITY_CAP)
 * - Minimum score: 0
 *
 * @param errorCount Number of errors in the pipeline
 * @param warningCount Number of warnings in the pipeline
 * @param nodeCount Total number of nodes in the pipeline
 * @returns Health score between 0 and 100
 */
export function calculateHealthScore(
  errorCount: number,
  warningCount: number,
  nodeCount: number
): number {
  let score = 100;

  // Errors: -ERROR_PENALTY points each (capped at -ERROR_CAP)
  score -= Math.min(errorCount * ERROR_PENALTY, ERROR_CAP);

  // Warnings: -WARNING_PENALTY points each (capped at -WARNING_CAP)
  score -= Math.min(warningCount * WARNING_PENALTY, WARNING_CAP);

  // Complexity penalty: >COMPLEXITY_THRESHOLD nodes = -COMPLEXITY_PENALTY per extra node
  if (nodeCount > COMPLEXITY_THRESHOLD) {
    score -= Math.min(
      (nodeCount - COMPLEXITY_THRESHOLD) * COMPLEXITY_PENALTY,
      COMPLEXITY_CAP,
    );
  }

  return Math.max(score, 0);
}

/**
 * Detect cycles in pipeline graph using DFS (Depth-First Search)
 *
 * Uses a recursion stack to track the current path and detect back edges,
 * which indicate cycles in a directed graph.
 *
 * @param nodes Array of pipeline nodes
 * @param edges Array of pipeline edges
 * @returns Array of cycles (each cycle is an array of node IDs forming the cycle)
 */
export function detectCycles(
  nodes: PipelineNode[],
  edges: PipelineEdge[]
): string[][] {
  const adjacencyList = new Map<string, string[]>();

  // Build adjacency list
  nodes.forEach(node => adjacencyList.set(node.id, []));
  edges.forEach(edge => {
    const neighbors = adjacencyList.get(edge.source) || [];
    neighbors.push(edge.target);
    adjacencyList.set(edge.source, neighbors);
  });

  const visited = new Set<string>();
  const recStack = new Set<string>();
  const cycles: string[][] = [];

  function dfs(nodeId: string, path: string[]): void {
    visited.add(nodeId);
    recStack.add(nodeId);
    path.push(nodeId);

    const neighbors = adjacencyList.get(nodeId) || [];
    for (const neighbor of neighbors) {
      if (!visited.has(neighbor)) {
        dfs(neighbor, path);
      } else if (recStack.has(neighbor)) {
        // Found a cycle
        const cycleStart = path.indexOf(neighbor);
        cycles.push([...path.slice(cycleStart), neighbor]);
      }
    }

    path.pop();
    recStack.delete(nodeId);
  }

  nodes.forEach(node => {
    if (!visited.has(node.id)) {
      dfs(node.id, []);
    }
  });

  return cycles;
}

/**
 * Find nodes that have no incoming or outgoing connections (orphan nodes)
 *
 * Orphan nodes are isolated from the pipeline flow and won't be executed.
 *
 * @param nodes Array of pipeline nodes
 * @param edges Array of pipeline edges
 * @returns Array of orphaned node IDs
 */
export function findOrphanNodes(
  nodes: PipelineNode[],
  edges: PipelineEdge[]
): string[] {
  const connectedNodes = new Set<string>();

  edges.forEach(edge => {
    connectedNodes.add(edge.source);
    connectedNodes.add(edge.target);
  });

  return nodes
    .filter(node => !connectedNodes.has(node.id))
    .map(node => node.id);
}

/**
 * Topological sort to determine execution order using Kahn's algorithm
 *
 * Uses in-degree based approach to determine a valid execution order for the pipeline.
 * Nodes are processed when all their dependencies have been processed.
 *
 * @param nodes Array of pipeline nodes
 * @param edges Array of pipeline edges
 * @returns Array of node IDs in execution order, or null if cycles detected
 */
export function topologicalSort(
  nodes: PipelineNode[],
  edges: PipelineEdge[]
): string[] | null {
  const inDegree = new Map<string, number>();
  const adjacencyList = new Map<string, string[]>();

  // Initialize
  nodes.forEach(node => {
    inDegree.set(node.id, 0);
    adjacencyList.set(node.id, []);
  });

  // Build graph
  edges.forEach(edge => {
    const neighbors = adjacencyList.get(edge.source) || [];
    neighbors.push(edge.target);
    adjacencyList.set(edge.source, neighbors);
    inDegree.set(edge.target, (inDegree.get(edge.target) || 0) + 1);
  });

  // Find all nodes with no incoming edges
  const queue: string[] = [];
  inDegree.forEach((degree, nodeId) => {
    if (degree === 0) queue.push(nodeId);
  });

  const result: string[] = [];

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current) break;

    result.push(current);

    const neighbors = adjacencyList.get(current) || [];
    neighbors.forEach(neighbor => {
      const newDegree = (inDegree.get(neighbor) || 0) - 1;
      inDegree.set(neighbor, newDegree);
      if (newDegree === 0) {
        queue.push(neighbor);
      }
    });
  }

  return result.length === nodes.length ? result : null;
}
